import React, { useState, useEffect, useMemo } from "react"
import TypeResolver from "../type-resolver" // Assuming TypeResolver is in the same directory
import ContentEditor from "../content-editor/index"
import DataTable from "aldoo-ra/DataTable" // Assuming you have a reusable DataTable component
import Typography from "aldoo-ra/Typography" // Assuming you have a reusable Typography component
import Button from "aldoo-ra/Button" // Assuming you have a reusable Button component
import Modal from "aldoo-ra/Modal"
import { showPromptDialog, showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import Tooltip from "../../Tooltip"
import { PlusCircleIcon, TrashIcon, PencilIcon, CloudArrowUpIcon } from "@heroicons/react/24/outline"
import { navigate } from "vike/client/router"
import { ChevronLeftIcon } from "@heroicons/react/24/outline"
import Controller from "../controllers/cms-controller"
import ContentTypeDefinition from "../schemas/type-schema"
import { showNotification } from "aldoo-ra/Notification"
import RestoreDialog from "../dialogs/restore-dialog"
import { GetCMSType } from "../api"
import { url } from "aldoo-ra/CMS/url"
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown"
import FilterPanel from "aldoo-ra/Admin/Updates/controller/FilterPanel"
import Checkbox from "aldoo-ra/Checkbox"
import moment from "moment"
import { buildCustomMatchFilters } from "aldoo-ra/CMS/utils/buildCustomMatchFilters"
import { pluginRegistry, createPluginContext, PluginDialog } from "aldoo-ra/CMS/plugins/index.jsx"
import ProgressDialog from "aldoo-ra/ProgressDialog"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import EnvironmentContentInstallDialog from "aldoo-ra/Admin/Updates/Environments/EnvironmentContentInstallDialog"

const separateWords = (text) => {
  if (!text) return
  // Insert a space before each uppercase letter, except the first one
  const separated = text.replace(/([A-Z])/g, " $1").trim()
  // Capitalize the first letter of the entire string
  return separated.charAt(0).toUpperCase() + separated.slice(1)
}

export default function TypeBrowser() {
  const [selectedInstance, setSelectedInstance] = useState(null) // To hold the selected content type
  const controller = Controller() // Initialize the Controller
  const [refreshKey, setRefreshKey] = useState(0) // To refresh the data table

  useEffect(() => {
    // Data table refresh triggered
  }, [refreshKey])
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false)
  const [dateFilter, setDateFilter] = useState({ type: "preset", value: null }) // Lifetime by default
  const [customFilters, setCustomFilters] = useState([])
  const [selectedTypes, setSelectedTypes] = useState([])
  const [selectedTypeData, setSelectedTypeData] = useState([])
  const [activePlugin, setActivePlugin] = useState(null)
  const [showPluginDialog, setShowPluginDialog] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progressData, setProgressData] = useState({ current: 0, total: 0, message: '' })
  const [showInstallDialog, setShowInstallDialog] = useState(false)

  const { isTypeUsed, hasDuplicates, getContentType } = TypeResolver() // Initialize the TypeResolver

  useEffect(() => {
    //when the controller does something, refresh the content in the data table
    controller.onRefreshContents(() => {
      setRefreshKey((prev) => prev + 1)
    })
  }, [])

  // Refresh data table when date filter changes
  useEffect(() => {
    setRefreshKey((prev) => prev + 1)
  }, [
    dateFilter.type,
    dateFilter.value,
    dateFilter.range?.startDate,
    dateFilter.range?.endDate,
  ])

  // Handle date filter changes
  const handleDateFilterChange = (newDateFilter) => {
    setDateFilter(newDateFilter)
  }


  // Build request fields with date filtering and custom filters
  const requestFields = useMemo(() => {
    const match = {
      deleted: { $ne: true },
      ...buildCustomMatchFilters(customFilters),
    }

    // Handle date filter
    if (dateFilter) {
      if (dateFilter.type === "preset" && dateFilter.value) {
        // Preset date (e.g., last 24h, last 7d)
        const { days } = dateFilter.value
        if (days) {
          // Use ISO string format that MongoDB can compare
          const cutoffDate = moment().subtract(days, "days").utc().toISOString()
          match.createdAt = { $gte: cutoffDate }
        }
        // If dateFilter.value is null (lifetime option), no date restriction
      } else if (dateFilter.type === "custom" && dateFilter.range) {
        // Custom date range
        const { startDate, endDate } = dateFilter.range
        if (startDate && endDate) {
          // Set the time of endDate to 23:59:59 to include the entire day
          const adjustedEndDate = moment(endDate)
            .utc()
            .endOf("day")
            .toISOString()
          const adjustedStartDate = moment(startDate)
            .utc()
            .startOf("day")
            .toISOString()
          match.createdAt = {
            $gte: adjustedStartDate,
            $lte: adjustedEndDate,
          }
        }
      }
    }

    return { match }
  }, [dateFilter, customFilters])

  //preprocess
  useEffect(() => {
    if (!selectedInstance) return

    //resolve groups from the the ContentType
    selectedInstance?.content?.fields?.forEach((field) => {
      //------------------------------------------------
      // Groups
      //------------------------------------------------
      if (field.group) {
        //find the group in the resolved content type
        const resolvedType = getContentType(selectedInstance.content.type)
        //find the group
        const matchGroup = resolvedType?.groups?.find(
          (group) => group.id === field.group
        )
        if (!matchGroup) return

        field.group = {
          value: matchGroup.id,
          name: matchGroup.label,
        }
      }
    })
  }, [selectedInstance])

  const deleteTypeAction = async (item) => {
    const { content } = item

    if (!content) return

    const usedIn = isTypeUsed(content.type)
    //generate a message (HTML) that shows where the content type is used
    //if it's not used anywhere, just show a simple message
    let message = usedIn.length ? (
      <>
        <p>{`${content.type} is used in the following content types:`}</p>
        <ul className="flex flex-col">
          {usedIn.map((item) => (
            <Tooltip key={item.type} content={item.label} position="top">
              <li key={item.type}>• {item.type}</li>
            </Tooltip>
          ))}
        </ul>
        <p>{`Are you sure you want to delete ${content.type}?`}</p>
      </>
    ) : (
      <p>{`Are you sure you want to delete ${content.type}?`}</p>
    )

    const { deleted, live } = await controller.getTypeUsage({
      type: content.type,
    })

    if (deleted > 0 || live > 0) {
      message = (
        <>
          <p>{`This type is used in:`}</p>
          <ul className="flex flex-col">
            {live > 0 && <li>• {`${live} live contents`}</li>}
            {deleted > 0 && <li>• {`${deleted} deleted contents`}</li>}
          </ul>
          <p>{`Are you sure you want to delete ${content.type}?`}</p>
        </>
      )
    }

    showPromptDialog({
      title: "Delete Content Type",
      message,
      confirmText: "Delete",
      onConfirm: () => {
        controller.deleteType(item._id)
      },
    })
  }

  // Selection handlers
  const handleSelectType = (item, isSelected) => {
    if (isSelected) {
      setSelectedTypes([...selectedTypes, item._id])
      setSelectedTypeData([...selectedTypeData, item])
    } else {
      setSelectedTypes(selectedTypes.filter(id => id !== item._id))
      setSelectedTypeData(selectedTypeData.filter(d => d._id !== item._id))
    }
  }

  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedTypes(items.map(d => d._id))
      setSelectedTypeData([...items])
    } else {
      setSelectedTypes([])
      setSelectedTypeData([])
    }
  }

  const handleBulkDelete = async () => {
    const result = await showPromptDialogAsync({
      title: "Delete Content Types",
      message: `Are you sure you want to delete ${selectedTypes.length} selected type${selectedTypes.length !== 1 ? 's' : ''}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
    })

    if (result) {
      try {
        await Promise.all(
          selectedTypes.map(typeId =>
            controller.deleteType(typeId)
          )
        )
        showNotification({
          message: `Successfully deleted ${selectedTypes.length} type${selectedTypes.length !== 1 ? 's' : ''}`,
          className: "bg-admin_success text-white",
        })
        setSelectedTypes([])
        setSelectedTypeData([])
        setRefreshKey((prev) => prev + 1)
      } catch (error) {
        showNotification({
          message: `Error deleting types: ${error.message}`,
          className: "bg-admin_error text-white",
        })
      }
    }
  }

  // Plugin handlers
  const handleRunPlugin = (pluginId) => {
    const plugin = pluginRegistry.get(pluginId)
    if (!plugin) {
      return
    }

    // Create a mock controller for CMS Types that uses SaveCMSType
    const typeController = {
      saveContent: async ({ payload }) => {
        // For types, we need to call SaveCMSType instead of SaveCMSContent
        const result = await controller.saveType(payload)
        return result
      }
    }

    // For CMSTypes, we need to add a fake content.type to make createPluginContext work
    // CMSTypes have structure: { _id, content: { type, label, fields, groups, ... } }
    const normalizedTypeData = selectedTypeData.map(item => ({
      ...item,
      content: {
        ...item.content,
        // Add a type field so createPluginContext can build the type map
        type: item.content?.type || 'CMSType'
      }
    }))

    // Create a synthetic contentType for CMSTypes using the type schema
    // Map the schema fields to type_definition format expected by plugins
    const syntheticContentType = {
      type: 'CMSType',
      type_definition: ContentTypeDefinition.fields.map(field => ({
        name: field.id,
        label: field.label,
        type: field.type?.toLowerCase() || 'text',
        description: field.description
      }))
    }

    // Create plugin context
    const context = createPluginContext(
      normalizedTypeData,
      syntheticContentType,
      typeController,
      {
        showDialog: (config) => {
          return Promise.resolve({})
        },
        showNotification: (message, type) => {
          showNotification({
            message,
            className: type === 'error' ? 'bg-admin_error text-white' :
                      type === 'success' ? 'bg-admin_success text-white' :
                      'bg-admin_info text-white',
          })
        },
        showProgress: (current, total, message) => {
          setProgressData({ current, total, message })
          if (!showProgress && total > 0) {
            setShowProgress(true)
          }
        },
        confirm: (message) => {
          return showPromptDialog({
            title: "Confirm",
            message,
            confirmText: "Yes",
            cancelText: "No",
          })
        },
      }
    )

    // Validate plugin can run
    const validation = pluginRegistry.validate(pluginId, context)
    if (!validation.valid) {
      showNotification({
        message: validation.errors?.[0]?.message || "Cannot run plugin",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Show plugin dialog
    setActivePlugin({ plugin, context })
    setShowPluginDialog(true)
  }

  const handlePluginExecute = async (values) => {
    if (!activePlugin) return

    try {
      const result = await pluginRegistry.run(
        activePlugin.plugin.id,
        activePlugin.context,
        { values }
      )

      // Close progress dialog
      setShowProgress(false)

      // Refresh the data table if items were modified
      if (result.itemsProcessed > 0) {
        setRefreshKey((prev) => prev + 1)
        setSelectedTypes([])
        setSelectedTypeData([])
      }

      return result
    } catch (error) {
      setShowProgress(false)
      throw error
    }
  }

  // Define the columns for rendering the data in the table
  const tableItemRender = [
    // Type
    {
      id: "type",
      title: "Type",
      headerClassName: "w-[100px]",
      render: (value, item) => {
        const preview = item?.content?.thumbnail

        return (
          <div
            className="flex flex-row items-center cursor-pointer group transition-colors duration-200"
            onClick={() => {
              // Set selected content instance for editing
              setSelectedInstance(item)
            }}
          >
            {preview && (
              <Tooltip
                content={
                  <div className="text-center">
                    <span className="font-bold text-admin_text">
                      {item?.content?.type}
                    </span>
                    <img
                      src={url(preview)}
                      className="w-[400px] h-[400px] object-contain mr-2 rounded-[10px]"
                    />
                  </div>
                }
                position="right"
              >
                <img
                  src={url(preview)}
                  className="w-10 h-10 mr-2 rounded-[10px] "
                />
              </Tooltip>
            )}
            <span className="text-admin_text group-hover:text-admin_primary transition-colors duration-200">{item?.content?.type}</span>
          </div>
        )
      },
    },

    // Label
    {
      id: "label",
      title: "Label",
      headerClassName: "w-[55%]",
      render: (value, item) => (
        <span className="text-admin_text">{item.content?.label}</span>
      ),
    },
    // Edit button
    {
      id: "edit",
      headerClassName: "w-7 text-center",
      render: (value, item) => (
        <Button
          variant="admin_icon"
          className="text-admin_text_secondary mx-auto"
          onClick={() => {
            // Set selected content instance for editing
            setSelectedInstance(item)
          }}
        >
          <PencilIcon className="w-5 h-5" />
        </Button>
      ),
    },
    // Delete button
    {
      id: "delete",
      headerClassName: "w-6",
      render: (value, field) => (
        <Button
          variant="admin_icon"
          className="text-admin_text_secondary hover:text-admin_error"
          onClick={async () => await deleteTypeAction(field)}
        >
          <TrashIcon className="w-5 h-5" />
        </Button>
      ),
    },
  ]
  // Custom button for adding new content
  const headerControls = (
    <div className="w-full flex items-end gap-3">
      <DateFilterDropdown
        onChange={handleDateFilterChange}
        variant="admin"
        value={dateFilter}
        className="w-[190px] -mt-8"
        styleOverrides={{
          label: "text-admin_text_secondary text-xs font-normal mb-0.5",
          header: "w-full flex justify-between items-end gap-2 mb-1",
        }}
      />
    </div>
  )

  const filterControl = (
    <FilterPanel
      value={customFilters}
      onChange={setCustomFilters}
      onApply={(value) => {
        setCustomFilters(value)
        setRefreshKey((prev) => prev + 1)
      }}
      onClear={() => {
        setCustomFilters([])
        setRefreshKey((prev) => prev + 1)
      }}
    />
  )

  const validators = (errors) => ({
    type: (value, item) => {
      //check if the content type already exists
      // Only block duplicates when creating NEW types (no _id)
      // Allow editing existing types (has _id)
      if (!item._id && hasDuplicates(item)) {
        //set the error
        errors.type =
          "Content type already exists. Please choose a different name."
      }
    },
  })

  return (
    <div className="p-4">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-5">
        <Typography
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Types
        </Typography>

        <div className="flex gap-2 items-center">
          <Button
            variant="admin_secondary"
            className="w-32 whitespace-nowrap"
            onClick={() => {
              setOpenRestoreDialog(true)
            }}
          >
            Restore
          </Button>

          <Button
            variant="admin_primary"
            className="whitespace-nowrap"
            onClick={() => {
              setSelectedInstance({
                content: {},
              })
            }}
          >
            <div className="flex items-center gap-1">
              <PlusCircleIcon className="w-5 h-5" />
              Add
            </div>
          </Button>
        </div>
      </div>

      {/* Data table for displaying types */}
      <DataTable
        variant="admin_browser"
        refreshKey={refreshKey}
        apiMethod={GetCMSType}
        preferredHeight="75vh"
        headerControls={headerControls} // Custom header controls
        filterControl={filterControl}
        itemHeight="h-[50px]"
        searchField="content.type" // Field to search
        requestFields={requestFields}
        searchObject={{}} // Search object
        sort={{ createdAt: -1 }} // Sort by creation date (descending)
        localUserPreferencesKey="cms.typeBrowser"
        renders={tableItemRender} // Columns definition
        enableSelection={true}
        selectedItems={selectedTypes}
        selectedData={selectedTypeData}
        onSelectionChange={(ids, data) => {
          setSelectedTypes(ids);
          setSelectedTypeData(data);
        }}
        tableFooter={
          selectedTypes.length > 0 ? (
            <div className="flex items-center justify-between p-4 border-t border-admin_bordered">
              <Typography variant="admin_body">
                {selectedTypes.length} item{selectedTypes.length !== 1 ? 's' : ''} selected
              </Typography>
              <div className="flex items-center gap-3">
                <DropDownSelect
                  options={pluginRegistry.getAll().map(plugin => ({
                    value: plugin.id,
                    label: plugin.name,
                    name: plugin.name,
                    description: plugin.description
                  }))}
                  placeholder="Actions"
                  variant="admin"
                  searchEnabled={true}
                  nameField="name"
                  onChange={(selected) => {
                    const selectedItem = Array.isArray(selected) ? selected[0] : selected
                    if (selectedItem && selectedItem.value) {
                      handleRunPlugin(selectedItem.value)
                    }
                  }}
                  renderOption={(option) => (
                    <Tooltip text={option.description} position="left" variant="dark">
                      <Typography variant="admin_body" className="text-sm">
                        {option.label}
                      </Typography>
                    </Tooltip>
                  )}
                  fixedWidth="200px"
                  className="min-w-[200px]"
                />
                <Button
                  variant="admin_primary"
                  onClick={() => setShowInstallDialog(true)}
                >
                  <div className="flex items-center gap-1">
                    <CloudArrowUpIcon className="w-4 h-4" />
                    Install on Environment
                  </div>
                </Button>
                <Button
                  variant="admin_secondary"
                  onClick={() => {
                    setSelectedTypes([])
                    setSelectedTypeData([])
                  }}
                >
                  Deselect
                </Button>
                <Button
                  variant="admin_error"
                  onClick={handleBulkDelete}
                >
                  <div className="flex items-center gap-1">
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </div>
                </Button>
              </div>
            </div>
          ) : undefined
        }
        itemIdField="_id"
        styleOverrides={{
          table: "w-full table-fixed",
          searchInput: "absolute top-6 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          filterControl: "absolute top-6 right-[370px]"
        }}
        noData={<Typography variant="admin_h3">No types available</Typography>}
      />

      {/* Modal for type designer */}
      <Modal
        isOpen={selectedInstance !== null}
        onClose={() => setSelectedInstance(null)}
        closeOnEscape
        variant="admin"
        className="w-full sm:w-[500px] md:w-[85%] md:absolute md:top-[15px] h-full md:h-auto"
      >
        {selectedInstance?.content && (() => {
          return (
          <ContentEditor
            title={separateWords(selectedInstance?.content.type)}
            enableFieldSettings={false}
            allowTypeImport
            validators={validators}
            contentType={ContentTypeDefinition}
            dataItem={selectedInstance}
            fieldValues={{
              ...selectedInstance?.content,
              _id: selectedInstance?._id,
            }}
            clearValuesAfterSave={false}
            onSave={async (data) => {
              // Preserve all original fields from selectedInstance and update content
              const saveData = {
                ...selectedInstance,  // Preserve all original fields (_id, __v, etc.)
                content: data        // Update content with new form data
              }

              const result = await controller.saveType(saveData)
              return result
            }}
            onClickSave={async (result) => {
              // Don't close the modal automatically on save, but refresh the content
              if (result && !result.error && selectedInstance?._id) {
                // Reload the updated instance from the database
                try {
                  const updatedData = await GetCMSType({ match: { _id: selectedInstance._id } })
                  if (updatedData && updatedData.data && updatedData.data[0]) {
                    setSelectedInstance(updatedData.data[0])
                  }
                } catch (error) {
                  console.error('Failed to reload content after save:', error)
                }
              }
            }}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-admin_text_inv",
              })
            }}
            onRefresh={async () => {
              // Reload the content from database when download completes
              if (selectedInstance?._id) {
                try {
                  const updatedData = await GetCMSType({ match: { _id: selectedInstance._id } })
                  if (updatedData && updatedData.data && updatedData.data[0]) {
                    setSelectedInstance(updatedData.data[0])
                  }
                } catch (error) {
                  console.error('Failed to reload content after download:', error)
                }
              }
            }}
          />
        )})()}
      </Modal>

      {/* Restore dialog */}
      <RestoreDialog
        isOpen={openRestoreDialog}
        onClose={() => setOpenRestoreDialog(false)}
        type={"type"}
        controller={controller}
      />

      {/* Plugin dialog */}
      {activePlugin && (
        <PluginDialog
          plugin={activePlugin.plugin}
          context={activePlugin.context}
          isOpen={showPluginDialog}
          onClose={() => {
            setShowPluginDialog(false)
            setActivePlugin(null)
          }}
          onExecute={handlePluginExecute}
        />
      )}

      {/* Progress dialog */}
      <ProgressDialog
        isOpen={showProgress}
        title="Processing Items"
        message={progressData.message}
        progress={progressData.total > 0 ? progressData.current / progressData.total : 0}
        onCancel={() => setShowProgress(false)}
        onClose={() => setShowProgress(false)}
      />

      {showInstallDialog && (
        <EnvironmentContentInstallDialog
          target={selectedTypeData.map(item => ({
            type: "CMSType",
            match: { "_id": item._id }
          }))}
          contentName={`Selection (${selectedTypeData.length} CMS type${selectedTypeData.length !== 1 ? 's' : ''})`}
          onClose={() => setShowInstallDialog(false)}
        />
      )}
    </div>
  )
}
